import { useEffect, useState } from "react";
import { GetV2Channel_getV2Channel_distributions } from "../__generated__/GetV2Channel";
import { Button, DialogActions } from "@mui/material";
import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../../../../BillingDetails/AddPaymentMethodDialog";
import BottomDialog from "../BottomDialog";
import gql from "graphql-tag";
import { Add, DeleteForeverOutlined } from "@mui/icons-material";
import DistributionAddJwtKeyDialog from "./DistributionAddJwtKeyDialog";
import ConfirmDeleteDialog from "../ConfirmDeleteDialog";
import {
    DeleteDistributionSecurityKeys,
    DeleteDistributionSecurityKeysVariables,
} from "./__generated__/DeleteDistributionSecurityKeys";
import { useMutation } from "react-apollo";
import { GET_V2_CHANNEL } from "../V2ChannelDetails";
import {
    DeleteDistributionSecurityKey,
    DeleteDistributionSecurityKeyVariables,
} from "./__generated__/DeleteDistributionSecurityKey";

interface Props {
    distribution: Omit<GetV2Channel_getV2Channel_distributions, "__typename">;
    open: boolean;
    organizationId: string;
    channelId: string;
    onClose: () => void;
    onConfirm: (keys: { id: string; name?: string }[]) => void;
}

export default function DistributionJwtDialog({
    distribution,
    onClose,
    open,
    organizationId,
    onConfirm,
    channelId,
}: Props) {
    const [selectedKeyId, setSelectedKeyId] = useState<string | undefined>();

    const [keys, setKeys] = useState<{ id: string; name?: string }[]>(
        distribution.security.keys
            ? distribution.security.keys.map((key) => ({ id: key.id, name: key.name !== null ? key.name : undefined }))
            : [],
    );

    const [openAddKeyDialog, setOpenAddKeyDialog] = useState<boolean>(false);
    const [openRemoveKeyDialog, setOpenRemoveKeyDialog] = useState<boolean>(false);
    const [openRemoveAllKeysDialog, setOpenRemoveAllKeysDialog] = useState<boolean>(false);

    const [removeAllKeys, { loading: loadingDeleteAll }] = useMutation<
        DeleteDistributionSecurityKeys,
        DeleteDistributionSecurityKeysVariables
    >(DELETE_ALL_DISTRIBUTION_SECURITY_KEYS);

    const [removeKey, { loading: loadingDelete }] = useMutation<
        DeleteDistributionSecurityKey,
        DeleteDistributionSecurityKeyVariables
    >(DELETE_DISTRIBUTION_SECURITY_KEY);

    useEffect(() => {
        setKeys(
            distribution.security.keys
                ? distribution.security.keys.map((key) => ({
                      id: key.id,
                      name: key.name !== null ? key.name : undefined,
                  }))
                : [],
        );
    }, [open, distribution]);

    async function onRemoveAllKeys(): Promise<void> {
        try {
            setOpenRemoveAllKeysDialog(false);
            await removeAllKeys({
                variables: { input: { id: distribution.id, organizationId } },
                refetchQueries: [
                    {
                        query: GET_V2_CHANNEL,
                        variables: {
                            organizationId,
                            channelId,
                        },
                    },
                ],
            });

            onConfirm([]);
            toast.success("JWT security updated");
        } catch (e: any) {
            toast.error(e.graphQLErrors[0].message ?? "Something went wrong 😞", TOAST_SETTINGS);
        }
    }

    async function onRemoveKey(): Promise<void> {
        if (selectedKeyId) {
            try {
                setOpenRemoveKeyDialog(false);
                await removeKey({
                    variables: { input: { id: distribution.id, organizationId, keyId: selectedKeyId } },
                    refetchQueries: [
                        {
                            query: GET_V2_CHANNEL,
                            variables: {
                                organizationId,
                                channelId,
                            },
                        },
                    ],
                });

                setKeys(keys.filter((k) => k.id !== selectedKeyId));
                if (keys.filter((k) => k.id !== selectedKeyId).length === 0) {
                    onConfirm([]);
                }

                toast.success("JWT security updated");
            } catch (e: any) {
                toast.error(e.graphQLErrors[0].message ?? "Something went wrong 😞", TOAST_SETTINGS);
            }
        }
    }

    return (
        <>
            <BottomDialog open={open} title="Manage JWT security" onClose={() => onClose()}>
                <div>
                    <div className="rounded-md bg-slate-50 px-2 py-2 flex flex-col gap-4">
                        <div className="flex flex-row w-full justify-between gap-2 items-center">
                            <div>
                                {keys.length === 0
                                    ? "JWT security disabled. Add a key to enable it"
                                    : "Active JWT keys"}
                            </div>
                            {keys.length > 0 && (
                                <div>
                                    <button
                                        onClick={() => setOpenRemoveAllKeysDialog(true)}
                                        className="bg-red-500 hover:bg-red-400 text-white rounded-md text-xs font-semibold py-2 px-2"
                                    >
                                        Remove JWT security
                                    </button>
                                </div>
                            )}
                        </div>
                        {keys.length > 0 && (
                            <div className="flex flex-row gap-4 flex-wrap">
                                {keys.map((key) => (
                                    <div
                                        onClick={() => {
                                            setSelectedKeyId(key.id);
                                            setOpenRemoveKeyDialog(true);
                                        }}
                                        className="cursor-pointer bg-white border-[1px] rounded-md border-gray-400 text-xs py-2 px-2 flex-row flex gap-3 items-center"
                                    >
                                        <div>
                                            <div className="font-semibold">{key.name}</div>
                                            <div className="font-mono">{key.id}</div>
                                        </div>
                                        <div>
                                            <button>
                                                <DeleteForeverOutlined />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {keys.length < 2 && (
                            <div>
                                <button
                                    onClick={() => setOpenAddKeyDialog(true)}
                                    className="bg-yellow-300 hover:bg-yellow-200 text-sm flex flex-row gap-1 items-center font-semibold rounded-md px-2 py-1"
                                >
                                    <Add style={{ fontSize: "16px" }} />
                                    Add key
                                </button>
                            </div>
                        )}
                    </div>
                    <DialogActions className=" flex flex-row gap-2 justify-end">
                        <Button
                            variant="contained"
                            color="inherit"
                            onClick={() => onConfirm(keys)}
                            disabled={loadingDeleteAll || loadingDelete}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </div>
            </BottomDialog>
            <DistributionAddJwtKeyDialog
                channelId={channelId}
                open={openAddKeyDialog}
                onClose={() => setOpenAddKeyDialog(false)}
                distributionId={distribution.id}
                organizationId={organizationId}
                onConfirm={(id: string, name?: string) => {
                    setOpenAddKeyDialog(false);
                    const newKeys = keys.concat([{ id, name }]);
                    setKeys(newKeys);
                }}
            />
            <ConfirmDeleteDialog
                title="Remove JWT security"
                content={<div>Are you sure you want to remove all keys and therefore disable JWT security?</div>}
                open={openRemoveAllKeysDialog}
                onClose={() => setOpenRemoveAllKeysDialog(false)}
                onConfirm={() => onRemoveAllKeys()}
            />
            <ConfirmDeleteDialog
                title="Remove JWT key"
                content={
                    <div>
                        Are you sure you want to this key?{" "}
                        {keys.length === 1 ? "As this is the only key, JWT security will be disabled completely" : ""}
                    </div>
                }
                open={openRemoveKeyDialog}
                onClose={() => setOpenRemoveKeyDialog(false)}
                onConfirm={() => onRemoveKey()}
            />
        </>
    );
}

export const ADD_DISTRIBUTION_SECURITY_KEY = gql`
    mutation AddDistributionSecurityKey($input: AddV2DistributionKeyInput!) {
        addV2DistributionKey(input: $input) {
            id
            name
        }
    }
`;

export const DELETE_DISTRIBUTION_SECURITY_KEY = gql`
    mutation DeleteDistributionSecurityKey($input: DeleteV2DistributionKeyInput!) {
        deleteV2DistributionKey(input: $input)
    }
`;

export const DELETE_ALL_DISTRIBUTION_SECURITY_KEYS = gql`
    mutation DeleteDistributionSecurityKeys($input: DeleteV2DistributionKeysInput!) {
        deleteV2DistributionKeys(input: $input)
    }
`;
